import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, Users, UserPlus, FileText, Calendar, AlertTriangle,
  Edit, Trash2, Eye, Upload, Download, Clock, CheckCircle, XCircle,
  GraduationCap, Briefcase, Heart, Phone, Mail, MapPin, CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and labels
const statusBadge = {
  ativo: { label: 'Ativo', class: 'bg-green-100 text-green-800' },
  inativo: { label: 'Inativo', class: 'bg-slate-100 text-slate-600' },
  ferias: { label: 'Férias', class: 'bg-blue-100 text-blue-800' },
  afastado: { label: 'Afastado', class: 'bg-amber-100 text-amber-800' },
};

const tipoContratoLabels = {
  clt: 'CLT',
  pj: 'PJ',
  temporario: 'Temporário',
  estagiario: 'Estagiário',
  autonomo: 'Autônomo',
};

const tipoDocumentoLabels = {
  rg: 'RG',
  cpf: 'CPF',
  ctps: 'Carteira de Trabalho',
  pis: 'PIS',
  comprovante_residencia: 'Comprovante de Residência',
  certidao_nascimento: 'Certidão de Nascimento',
  certidao_casamento: 'Certidão de Casamento',
  certificado: 'Certificado',
  diploma: 'Diploma',
  exame_admissional: 'Exame Admissional',
  exame_periodico: 'Exame Periódico',
  exame_demissional: 'Exame Demissional',
  carteira_profissional: 'Carteira Profissional',
  outros: 'Outros',
};

export default function RHPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('funcionarios');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [funcionarios, setFuncionarios] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [treinamentos, setTreinamentos] = useState([]);
  const [ferias, setFerias] = useState([]);
  const [todosDocumentos, setTodosDocumentos] = useState([]);
  
  // Dialogs
  const [showFuncionarioDialog, setShowFuncionarioDialog] = useState(false);
  const [showDetalhesFuncionarioDialog, setShowDetalhesFuncionarioDialog] = useState(false);
  const [showDocumentoDialog, setShowDocumentoDialog] = useState(false);
  const [showAtestadoDialog, setShowAtestadoDialog] = useState(false);
  const [showFeriasDialog, setShowFeriasDialog] = useState(false);
  const [showTreinamentoDialog, setShowTreinamentoDialog] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [editingFuncionario, setEditingFuncionario] = useState(null);
  
  // Funcionario form
  const initialFuncionarioForm = {
    nome: '',
    cpf: '',
    rg: '',
    data_nascimento: '',
    estado_civil: 'solteiro',
    telefone: '',
    email: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    carteira_profissional: '',
    conselho_classe: '',
    pis: '',
    ctps_numero: '',
    ctps_serie: '',
    cargo: '',
    departamento: '',
    data_admissao: new Date().toISOString().split('T')[0],
    tipo_contrato: 'clt',
    salario_base: 0,
    carga_horaria: 44,
    observacoes: '',
  };
  
  const [funcionarioForm, setFuncionarioForm] = useState(initialFuncionarioForm);
  
  // Documento form
  const [documentoForm, setDocumentoForm] = useState({
    tipo: 'rg',
    nome: '',
    descricao: '',
    data_emissao: '',
    data_validade: '',
    arquivo: null,
  });
  
  // Atestado form
  const [atestadoForm, setAtestadoForm] = useState({
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
    dias_afastamento: 1,
    cid: '',
    descricao_cid: '',
    nome_medico: '',
    crm_medico: '',
    observacoes: '',
  });
  
  // Férias form
  const [feriasForm, setFeriasForm] = useState({
    funcionario_id: '',
    periodo_aquisitivo_inicio: '',
    periodo_aquisitivo_fim: '',
    data_inicio: '',
    data_fim: '',
    dias_gozo: 30,
    dias_abono: 0,
    observacoes: '',
  });
  
  // Treinamento form
  const [treinamentoForm, setTreinamentoForm] = useState({
    titulo: '',
    descricao: '',
    instrutor: '',
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: '',
    carga_horaria: 0,
    local: '',
    funcionarios_ids: [],
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [funcRes, alertRes, statsRes, treinRes, feriasRes] = await Promise.all([
        fetch(`${API_URL}/api/rh/funcionarios`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/rh/alertas`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/rh/estatisticas`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/rh/treinamentos`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/rh/ferias`, { headers: getAuthHeaders() }),
      ]);
      
      let funcionariosData = [];
      if (funcRes.ok) {
        funcionariosData = await funcRes.json();
        setFuncionarios(funcionariosData);
        
        // Buscar documentos de todos os funcionários
        const docsPromises = funcionariosData.map(func => 
          fetch(`${API_URL}/api/rh/funcionarios/${func.id}/documentos`, { headers: getAuthHeaders() })
            .then(res => res.ok ? res.json() : [])
            .then(docs => docs.map(doc => ({ ...doc, funcionario_nome: func.nome })))
        );
        const allDocs = await Promise.all(docsPromises);
        setTodosDocumentos(allDocs.flat());
      }
      if (alertRes.ok) setAlertas(await alertRes.json());
      if (statsRes.ok) setEstatisticas(await statsRes.json());
      if (treinRes.ok) setTreinamentos(await treinRes.json());
      if (feriasRes.ok) setFerias(await feriasRes.json());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // ============= FUNCIONÁRIOS =============

  const handleCreateFuncionario = async () => {
    if (!funcionarioForm.nome || !funcionarioForm.cpf) {
      toast.error('Nome e CPF são obrigatórios');
      return;
    }
    
    try {
      const method = editingFuncionario ? 'PUT' : 'POST';
      const url = editingFuncionario 
        ? `${API_URL}/api/rh/funcionarios/${editingFuncionario.id}`
        : `${API_URL}/api/rh/funcionarios`;
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(funcionarioForm)
      });
      
      if (res.ok) {
        toast.success(editingFuncionario ? 'Funcionário atualizado!' : 'Funcionário cadastrado!');
        setShowFuncionarioDialog(false);
        setEditingFuncionario(null);
        setFuncionarioForm(initialFuncionarioForm);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar funcionário');
      }
    } catch (error) {
      toast.error('Erro ao salvar funcionário');
    }
  };

  const handleEditFuncionario = (func) => {
    setEditingFuncionario(func);
    setFuncionarioForm({
      nome: func.nome || '',
      cpf: func.cpf || '',
      rg: func.rg || '',
      data_nascimento: func.data_nascimento || '',
      estado_civil: func.estado_civil || 'solteiro',
      telefone: func.telefone || '',
      email: func.email || '',
      endereco: func.endereco || '',
      cidade: func.cidade || '',
      estado: func.estado || '',
      cep: func.cep || '',
      carteira_profissional: func.carteira_profissional || '',
      conselho_classe: func.conselho_classe || '',
      pis: func.pis || '',
      ctps_numero: func.ctps_numero || '',
      ctps_serie: func.ctps_serie || '',
      cargo: func.cargo || '',
      departamento: func.departamento || '',
      data_admissao: func.data_admissao || '',
      tipo_contrato: func.tipo_contrato || 'clt',
      salario_base: func.salario_base || 0,
      carga_horaria: func.carga_horaria || 44,
      observacoes: func.observacoes || '',
    });
    setShowFuncionarioDialog(true);
  };

  const handleDeleteFuncionario = async (id) => {
    if (!window.confirm('Tem certeza que deseja desativar este funcionário?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/rh/funcionarios/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Funcionário desativado');
        fetchData();
      } else {
        toast.error('Erro ao desativar funcionário');
      }
    } catch (error) {
      toast.error('Erro ao desativar funcionário');
    }
  };

  const handleViewFuncionario = async (func) => {
    try {
      // Fetch documents for this employee
      const docsRes = await fetch(`${API_URL}/api/rh/funcionarios/${func.id}/documentos`, {
        headers: getAuthHeaders()
      });
      const atestadosRes = await fetch(`${API_URL}/api/rh/funcionarios/${func.id}/atestados`, {
        headers: getAuthHeaders()
      });
      const feriasRes = await fetch(`${API_URL}/api/rh/funcionarios/${func.id}/ferias`, {
        headers: getAuthHeaders()
      });
      
      let documentos = [];
      let atestados = [];
      let feriasFunc = [];
      
      if (docsRes.ok) documentos = await docsRes.json();
      if (atestadosRes.ok) atestados = await atestadosRes.json();
      if (feriasRes.ok) feriasFunc = await feriasRes.json();
      
      setSelectedFuncionario({ ...func, documentos, atestados, ferias: feriasFunc });
      setShowDetalhesFuncionarioDialog(true);
    } catch (error) {
      setSelectedFuncionario(func);
      setShowDetalhesFuncionarioDialog(true);
    }
  };

  // ============= DOCUMENTOS =============

  const handleAddDocumento = async () => {
    if (!selectedFuncionario) {
      toast.error('Selecione um funcionário');
      return;
    }
    if (!documentoForm.nome || !documentoForm.tipo) {
      toast.error('Nome e tipo do documento são obrigatórios');
      return;
    }
    
    try {
      const payload = {
        ...documentoForm,
        funcionario_id: selectedFuncionario.id,
      };
      
      // If file selected, convert to base64
      if (documentoForm.arquivo) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          payload.arquivo_base64 = e.target.result;
          delete payload.arquivo;
          
          const res = await fetch(`${API_URL}/api/rh/funcionarios/${selectedFuncionario.id}/documentos`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
          });
          
          if (res.ok) {
            toast.success('Documento adicionado!');
            setShowDocumentoDialog(false);
            setSelectedFuncionario(null);
            setDocumentoForm({ tipo: 'rg', nome: '', descricao: '', data_emissao: '', data_validade: '', arquivo: null });
            fetchData(); // Recarrega todos os dados incluindo documentos
          } else {
            const error = await res.json();
            toast.error(error.detail || 'Erro ao adicionar documento');
          }
        };
        reader.readAsDataURL(documentoForm.arquivo);
      } else {
        delete payload.arquivo;
        const res = await fetch(`${API_URL}/api/rh/funcionarios/${selectedFuncionario.id}/documentos`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        
        if (res.ok) {
          toast.success('Documento adicionado!');
          setShowDocumentoDialog(false);
          setSelectedFuncionario(null);
          setDocumentoForm({ tipo: 'rg', nome: '', descricao: '', data_emissao: '', data_validade: '', arquivo: null });
          fetchData(); // Recarrega todos os dados incluindo documentos
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Erro ao adicionar documento');
        }
      }
    } catch (error) {
      toast.error('Erro ao adicionar documento');
    }
  };

  // ============= ATESTADOS =============

  const handleAddAtestado = async () => {
    if (!atestadoForm.data_inicio || !atestadoForm.data_fim) {
      toast.error('Datas de início e fim são obrigatórias');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/rh/atestados`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...atestadoForm,
          funcionario_id: selectedFuncionario.id,
        })
      });
      
      if (res.ok) {
        toast.success('Atestado registrado!');
        setShowAtestadoDialog(false);
        setAtestadoForm({
          data_inicio: new Date().toISOString().split('T')[0],
          data_fim: new Date().toISOString().split('T')[0],
          dias_afastamento: 1,
          cid: '',
          descricao_cid: '',
          nome_medico: '',
          crm_medico: '',
          observacoes: '',
        });
        handleViewFuncionario(selectedFuncionario);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao registrar atestado');
      }
    } catch (error) {
      toast.error('Erro ao registrar atestado');
    }
  };

  // ============= FÉRIAS =============

  const handleCreateFerias = async () => {
    if (!feriasForm.funcionario_id || !feriasForm.data_inicio) {
      toast.error('Funcionário e data de início são obrigatórios');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/rh/ferias`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(feriasForm)
      });
      
      if (res.ok) {
        toast.success('Férias programadas!');
        setShowFeriasDialog(false);
        setFeriasForm({
          funcionario_id: '',
          periodo_aquisitivo_inicio: '',
          periodo_aquisitivo_fim: '',
          data_inicio: '',
          data_fim: '',
          dias_gozo: 30,
          dias_abono: 0,
          observacoes: '',
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao programar férias');
      }
    } catch (error) {
      toast.error('Erro ao programar férias');
    }
  };

  // ============= TREINAMENTOS =============

  const handleCreateTreinamento = async () => {
    if (!treinamentoForm.titulo || !treinamentoForm.data_inicio) {
      toast.error('Título e data de início são obrigatórios');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/rh/treinamentos`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(treinamentoForm)
      });
      
      if (res.ok) {
        toast.success('Treinamento criado!');
        setShowTreinamentoDialog(false);
        setTreinamentoForm({
          titulo: '',
          descricao: '',
          instrutor: '',
          data_inicio: new Date().toISOString().split('T')[0],
          data_fim: '',
          carga_horaria: 0,
          local: '',
          funcionarios_ids: [],
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar treinamento');
      }
    } catch (error) {
      toast.error('Erro ao criar treinamento');
    }
  };

  // Filtering
  const filteredFuncionarios = funcionarios.filter(f =>
    f.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.cpf?.includes(searchTerm) ||
    f.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.departamento?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="rh-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Pessoas</h1>
          <p className="text-slate-500">Funcionários, documentos, atestados e férias</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar funcionário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-600 font-medium">Funcionários Ativos</p>
                <p className="text-2xl font-bold text-blue-900">
                  {estatisticas?.funcionarios?.total_ativos || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-sm text-amber-600 font-medium">Atestados (Mês)</p>
                <p className="text-2xl font-bold text-amber-900">
                  {estatisticas?.atestados?.quantidade_mes || 0}
                </p>
                <p className="text-xs text-amber-600">{estatisticas?.atestados?.dias_afastamento_mes || 0} dias</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-green-600 font-medium">Férias Programadas</p>
                <p className="text-2xl font-bold text-green-900">
                  {estatisticas?.ferias?.programadas || 0}
                </p>
                <p className="text-xs text-green-600">{estatisticas?.ferias?.em_gozo || 0} em gozo</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-purple-600 font-medium">Treinamentos</p>
                <p className="text-2xl font-bold text-purple-900">
                  {estatisticas?.treinamentos?.agendados || 0}
                </p>
                <p className="text-xs text-purple-600">agendados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alertas ({alertas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {alertas.slice(0, 5).map((alerta) => (
                <div 
                  key={alerta.id} 
                  className={`p-2 rounded flex items-center justify-between ${
                    alerta.prioridade === 'alta' ? 'bg-red-100' : 'bg-amber-100'
                  }`}
                >
                  <div>
                    <span className="font-medium">{alerta.titulo}</span>
                    <span className="text-sm text-slate-600 ml-2">- {alerta.funcionario_nome}</span>
                    <p className="text-xs text-slate-500">{alerta.descricao}</p>
                  </div>
                  <Badge className={alerta.prioridade === 'alta' ? 'bg-red-600' : 'bg-amber-600'}>
                    {alerta.prioridade}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="funcionarios" data-testid="tab-funcionarios">
            <Users className="w-4 h-4 mr-2" />
            Funcionários
          </TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">
            <FileText className="w-4 h-4 mr-2" />
            Documentos
          </TabsTrigger>
          <TabsTrigger value="ferias" data-testid="tab-ferias">
            <Calendar className="w-4 h-4 mr-2" />
            Férias
          </TabsTrigger>
          <TabsTrigger value="treinamentos" data-testid="tab-treinamentos">
            <GraduationCap className="w-4 h-4 mr-2" />
            Treinamentos
          </TabsTrigger>
          <TabsTrigger value="alertas" data-testid="tab-alertas">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alertas
          </TabsTrigger>
        </TabsList>

        {/* ============= FUNCIONÁRIOS TAB ============= */}
        <TabsContent value="funcionarios" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { setEditingFuncionario(null); setFuncionarioForm(initialFuncionarioForm); setShowFuncionarioDialog(true); }}
              data-testid="btn-novo-funcionario"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Novo Funcionário
            </Button>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-slate-500">
                      <th className="pb-3 font-medium">Nome</th>
                      <th className="pb-3 font-medium">CPF</th>
                      <th className="pb-3 font-medium">Cargo</th>
                      <th className="pb-3 font-medium">Departamento</th>
                      <th className="pb-3 font-medium">Contrato</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          Carregando...
                        </td>
                      </tr>
                    ) : filteredFuncionarios.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          Nenhum funcionário encontrado
                        </td>
                      </tr>
                    ) : (
                      filteredFuncionarios.map((func) => (
                        <tr key={func.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 font-medium">{func.nome}</td>
                          <td className="py-3 text-sm">{func.cpf}</td>
                          <td className="py-3 text-sm">{func.cargo || '-'}</td>
                          <td className="py-3 text-sm">{func.departamento || '-'}</td>
                          <td className="py-3">
                            <Badge variant="outline">
                              {tipoContratoLabels[func.tipo_contrato] || func.tipo_contrato}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <Badge className={func.ativo ? statusBadge.ativo.class : statusBadge.inativo.class}>
                              {func.ativo ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleViewFuncionario(func)}
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedFuncionario(func);
                                  setShowDocumentoDialog(true);
                                }}
                                title="Adicionar documento"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                              >
                                <Upload className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleEditFuncionario(func)}
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleDeleteFuncionario(func.id)}
                                title="Desativar"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= DOCUMENTOS TAB ============= */}
        <TabsContent value="documentos" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Total: {todosDocumentos.length} documento(s) cadastrado(s)
            </p>
            <Button 
              onClick={() => {
                if (funcionarios.length === 0) {
                  toast.error('Cadastre um funcionário primeiro');
                  return;
                }
                setSelectedFuncionario(null); // Deixa null para permitir seleção no dialog
                setShowDocumentoDialog(true);
              }}
              data-testid="btn-novo-documento"
            >
              <Upload className="w-4 h-4 mr-2" />
              Novo Documento
            </Button>
          </div>

          <Card>
            <CardContent className="pt-4">
              {todosDocumentos.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Nenhum documento cadastrado</p>
                  <p className="text-sm mt-1">Clique em "Novo Documento" ou no ícone de upload na tabela de funcionários</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-slate-500">
                        <th className="pb-3 font-medium">Funcionário</th>
                        <th className="pb-3 font-medium">Documento</th>
                        <th className="pb-3 font-medium">Tipo</th>
                        <th className="pb-3 font-medium">Validade</th>
                        <th className="pb-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todosDocumentos.map((doc) => {
                        const isVencido = doc.data_validade && new Date(doc.data_validade) < new Date();
                        const isVencendo = doc.data_validade && !isVencido && 
                          new Date(doc.data_validade) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                        return (
                          <tr key={doc.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 font-medium">{doc.funcionario_nome}</td>
                            <td className="py-3">{doc.nome}</td>
                            <td className="py-3">
                              <Badge variant="outline">
                                {tipoDocumentoLabels[doc.tipo] || doc.tipo}
                              </Badge>
                            </td>
                            <td className="py-3 text-sm">
                              {doc.data_validade || 'Sem validade'}
                            </td>
                            <td className="py-3">
                              <Badge className={
                                isVencido ? 'bg-red-100 text-red-800' :
                                isVencendo ? 'bg-amber-100 text-amber-800' :
                                'bg-green-100 text-green-800'
                              }>
                                {isVencido ? 'Vencido' : isVencendo ? 'Vencendo' : 'Válido'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= FÉRIAS TAB ============= */}
        <TabsContent value="ferias" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowFeriasDialog(true)} data-testid="btn-nova-ferias">
              <Plus className="w-4 h-4 mr-2" />
              Programar Férias
            </Button>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-slate-500">
                      <th className="pb-3 font-medium">Funcionário</th>
                      <th className="pb-3 font-medium">Período Aquisitivo</th>
                      <th className="pb-3 font-medium">Início</th>
                      <th className="pb-3 font-medium">Fim</th>
                      <th className="pb-3 font-medium">Dias</th>
                      <th className="pb-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ferias.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-500">
                          Nenhuma programação de férias
                        </td>
                      </tr>
                    ) : (
                      ferias.map((f) => (
                        <tr key={f.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 font-medium">{f.funcionario_nome || '-'}</td>
                          <td className="py-3 text-sm">
                            {f.periodo_aquisitivo_inicio} a {f.periodo_aquisitivo_fim}
                          </td>
                          <td className="py-3 text-sm">{f.data_inicio}</td>
                          <td className="py-3 text-sm">{f.data_fim}</td>
                          <td className="py-3 text-sm">{f.dias_gozo}</td>
                          <td className="py-3">
                            <Badge className={
                              f.status === 'em_gozo' ? 'bg-blue-100 text-blue-800' :
                              f.status === 'concluida' ? 'bg-green-100 text-green-800' :
                              f.status === 'cancelada' ? 'bg-red-100 text-red-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {f.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= TREINAMENTOS TAB ============= */}
        <TabsContent value="treinamentos" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowTreinamentoDialog(true)} data-testid="btn-novo-treinamento">
              <Plus className="w-4 h-4 mr-2" />
              Novo Treinamento
            </Button>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-slate-500">
                      <th className="pb-3 font-medium">Título</th>
                      <th className="pb-3 font-medium">Instrutor</th>
                      <th className="pb-3 font-medium">Data</th>
                      <th className="pb-3 font-medium">Carga Horária</th>
                      <th className="pb-3 font-medium">Participantes</th>
                      <th className="pb-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {treinamentos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-500">
                          Nenhum treinamento cadastrado
                        </td>
                      </tr>
                    ) : (
                      treinamentos.map((t) => (
                        <tr key={t.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 font-medium">{t.titulo}</td>
                          <td className="py-3 text-sm">{t.instrutor || '-'}</td>
                          <td className="py-3 text-sm">{t.data_inicio}</td>
                          <td className="py-3 text-sm">{t.carga_horaria}h</td>
                          <td className="py-3 text-sm">{t.funcionarios_ids?.length || 0}</td>
                          <td className="py-3">
                            <Badge className={
                              t.status === 'concluido' ? 'bg-green-100 text-green-800' :
                              t.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                              t.status === 'cancelado' ? 'bg-red-100 text-red-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {t.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= ALERTAS TAB ============= */}
        <TabsContent value="alertas" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              {alertas.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p>Nenhum alerta pendente</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alertas.map((alerta) => (
                    <div 
                      key={alerta.id} 
                      className={`p-4 rounded-lg border ${
                        alerta.prioridade === 'alta' ? 'bg-red-50 border-red-200' :
                        alerta.prioridade === 'media' ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <AlertTriangle className={`w-4 h-4 ${
                              alerta.prioridade === 'alta' ? 'text-red-600' : 'text-amber-600'
                            }`} />
                            <span className="font-medium">{alerta.titulo}</span>
                            <Badge className={
                              alerta.prioridade === 'alta' ? 'bg-red-600' :
                              alerta.prioridade === 'media' ? 'bg-amber-600' :
                              'bg-slate-600'
                            }>
                              {alerta.prioridade}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{alerta.descricao}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Funcionário: {alerta.funcionario_nome} | Data: {alerta.data_referencia}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============= DIALOG FUNCIONÁRIO ============= */}
      <Dialog open={showFuncionarioDialog} onOpenChange={setShowFuncionarioDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFuncionario ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
            <DialogDescription>
              Preencha os dados do funcionário
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="pessoais" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="pessoais">Dados Pessoais</TabsTrigger>
              <TabsTrigger value="profissionais">Dados Profissionais</TabsTrigger>
              <TabsTrigger value="funcionais">Dados Funcionais</TabsTrigger>
            </TabsList>
            
            <TabsContent value="pessoais" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome Completo *</Label>
                  <Input
                    value={funcionarioForm.nome}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Nome completo"
                    data-testid="input-nome"
                  />
                </div>
                <div>
                  <Label>CPF *</Label>
                  <Input
                    value={funcionarioForm.cpf}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, cpf: e.target.value }))}
                    placeholder="000.000.000-00"
                    data-testid="input-cpf"
                  />
                </div>
                <div>
                  <Label>RG</Label>
                  <Input
                    value={funcionarioForm.rg}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, rg: e.target.value }))}
                    placeholder="RG"
                  />
                </div>
                <div>
                  <Label>Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={funcionarioForm.data_nascimento}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, data_nascimento: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Estado Civil</Label>
                  <Select
                    value={funcionarioForm.estado_civil}
                    onValueChange={(value) => setFuncionarioForm(prev => ({ ...prev, estado_civil: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solteiro">Solteiro(a)</SelectItem>
                      <SelectItem value="casado">Casado(a)</SelectItem>
                      <SelectItem value="divorciado">Divorciado(a)</SelectItem>
                      <SelectItem value="viuvo">Viúvo(a)</SelectItem>
                      <SelectItem value="uniao_estavel">União Estável</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={funcionarioForm.telefone}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, telefone: e.target.value }))}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={funcionarioForm.email}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Endereço</Label>
                  <Input
                    value={funcionarioForm.endereco}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, endereco: e.target.value }))}
                    placeholder="Rua, número, complemento"
                  />
                </div>
                <div>
                  <Label>Cidade</Label>
                  <Input
                    value={funcionarioForm.cidade}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, cidade: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Input
                    value={funcionarioForm.estado}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, estado: e.target.value }))}
                    placeholder="UF"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label>CEP</Label>
                  <Input
                    value={funcionarioForm.cep}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, cep: e.target.value }))}
                    placeholder="00000-000"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="profissionais" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Carteira Profissional (Nº)</Label>
                  <Input
                    value={funcionarioForm.carteira_profissional}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, carteira_profissional: e.target.value }))}
                    placeholder="Número do conselho"
                  />
                </div>
                <div>
                  <Label>Conselho de Classe</Label>
                  <Input
                    value={funcionarioForm.conselho_classe}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, conselho_classe: e.target.value }))}
                    placeholder="COREN, CRM, CRO, etc."
                  />
                </div>
                <div>
                  <Label>PIS/PASEP</Label>
                  <Input
                    value={funcionarioForm.pis}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, pis: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>CTPS - Número</Label>
                  <Input
                    value={funcionarioForm.ctps_numero}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, ctps_numero: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>CTPS - Série</Label>
                  <Input
                    value={funcionarioForm.ctps_serie}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, ctps_serie: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="funcionais" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cargo</Label>
                  <Input
                    value={funcionarioForm.cargo}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, cargo: e.target.value }))}
                    placeholder="Ex: Técnico de Enfermagem"
                  />
                </div>
                <div>
                  <Label>Departamento</Label>
                  <Input
                    value={funcionarioForm.departamento}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, departamento: e.target.value }))}
                    placeholder="Ex: CME"
                  />
                </div>
                <div>
                  <Label>Data de Admissão</Label>
                  <Input
                    type="date"
                    value={funcionarioForm.data_admissao}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, data_admissao: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Tipo de Contrato</Label>
                  <Select
                    value={funcionarioForm.tipo_contrato}
                    onValueChange={(value) => setFuncionarioForm(prev => ({ ...prev, tipo_contrato: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clt">CLT</SelectItem>
                      <SelectItem value="pj">PJ</SelectItem>
                      <SelectItem value="temporario">Temporário</SelectItem>
                      <SelectItem value="estagiario">Estagiário</SelectItem>
                      <SelectItem value="autonomo">Autônomo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Salário Base (R$)</Label>
                  <Input
                    type="number" onFocus={(e) => e.target.select()}
                    value={funcionarioForm.salario_base}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, salario_base: parseFloat(e.target.value) || 0 }))}
                    min={0}
                    step={0.01}
                  />
                </div>
                <div>
                  <Label>Carga Horária (horas/semana)</Label>
                  <Input
                    type="number" onFocus={(e) => e.target.select()}
                    value={funcionarioForm.carga_horaria}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, carga_horaria: parseInt(e.target.value) || 44 }))}
                    min={0}
                    max={44}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={funcionarioForm.observacoes}
                    onChange={(e) => setFuncionarioForm(prev => ({ ...prev, observacoes: e.target.value }))}
                    placeholder="Observações gerais"
                    rows={3}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowFuncionarioDialog(false); setEditingFuncionario(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreateFuncionario} data-testid="btn-salvar-funcionario">
              {editingFuncionario ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= DIALOG DETALHES FUNCIONÁRIO ============= */}
      <Dialog open={showDetalhesFuncionarioDialog} onOpenChange={setShowDetalhesFuncionarioDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Funcionário</DialogTitle>
          </DialogHeader>
          
          {selectedFuncionario && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="info">Informações</TabsTrigger>
                <TabsTrigger value="documentos">Documentos</TabsTrigger>
                <TabsTrigger value="atestados">Atestados</TabsTrigger>
                <TabsTrigger value="ferias">Férias</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Dados Pessoais
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p><strong>Nome:</strong> {selectedFuncionario.nome}</p>
                      <p><strong>CPF:</strong> {selectedFuncionario.cpf}</p>
                      <p><strong>RG:</strong> {selectedFuncionario.rg || '-'}</p>
                      <p><strong>Nascimento:</strong> {selectedFuncionario.data_nascimento || '-'}</p>
                      <p><strong>Estado Civil:</strong> {selectedFuncionario.estado_civil}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Contato
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p><strong>Telefone:</strong> {selectedFuncionario.telefone || '-'}</p>
                      <p><strong>Email:</strong> {selectedFuncionario.email || '-'}</p>
                      <p><strong>Endereço:</strong> {selectedFuncionario.endereco || '-'}</p>
                      <p><strong>Cidade:</strong> {selectedFuncionario.cidade || '-'} / {selectedFuncionario.estado || '-'}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Dados Funcionais
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p><strong>Cargo:</strong> {selectedFuncionario.cargo || '-'}</p>
                      <p><strong>Departamento:</strong> {selectedFuncionario.departamento || '-'}</p>
                      <p><strong>Admissão:</strong> {selectedFuncionario.data_admissao || '-'}</p>
                      <p><strong>Contrato:</strong> {tipoContratoLabels[selectedFuncionario.tipo_contrato]}</p>
                      <p><strong>Carga Horária:</strong> {selectedFuncionario.carga_horaria}h/semana</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" />
                        Dados Profissionais
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p><strong>Conselho:</strong> {selectedFuncionario.conselho_classe || '-'}</p>
                      <p><strong>Registro:</strong> {selectedFuncionario.carteira_profissional || '-'}</p>
                      <p><strong>PIS:</strong> {selectedFuncionario.pis || '-'}</p>
                      <p><strong>CTPS:</strong> {selectedFuncionario.ctps_numero || '-'} / {selectedFuncionario.ctps_serie || '-'}</p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="documentos" className="pt-4">
                <div className="flex justify-end mb-4">
                  <Button size="sm" onClick={() => setShowDocumentoDialog(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Adicionar Documento
                  </Button>
                </div>
                {selectedFuncionario.documentos?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedFuncionario.documentos.map((doc) => (
                      <Card key={doc.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{doc.nome}</p>
                            <p className="text-sm text-slate-500">
                              Tipo: {tipoDocumentoLabels[doc.tipo]} | 
                              Validade: {doc.data_validade || 'Sem validade'}
                            </p>
                          </div>
                          <Badge className={
                            doc.data_validade && new Date(doc.data_validade) < new Date() 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }>
                            {doc.data_validade && new Date(doc.data_validade) < new Date() ? 'Vencido' : 'Válido'}
                          </Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">Nenhum documento cadastrado</p>
                )}
              </TabsContent>
              
              <TabsContent value="atestados" className="pt-4">
                <div className="flex justify-end mb-4">
                  <Button size="sm" onClick={() => setShowAtestadoDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Atestado
                  </Button>
                </div>
                {selectedFuncionario.atestados?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedFuncionario.atestados.map((at) => (
                      <Card key={at.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{at.data_inicio} a {at.data_fim}</p>
                            <p className="text-sm text-slate-500">
                              {at.dias_afastamento} dia(s) | CID: {at.cid || '-'} | 
                              Médico: {at.nome_medico || '-'}
                            </p>
                          </div>
                          <Badge>{at.dias_afastamento} dias</Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">Nenhum atestado registrado</p>
                )}
              </TabsContent>
              
              <TabsContent value="ferias" className="pt-4">
                {selectedFuncionario.ferias?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedFuncionario.ferias.map((f) => (
                      <Card key={f.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{f.data_inicio} a {f.data_fim}</p>
                            <p className="text-sm text-slate-500">
                              {f.dias_gozo} dias de gozo | {f.dias_abono} dias de abono
                            </p>
                          </div>
                          <Badge className={
                            f.status === 'em_gozo' ? 'bg-blue-100 text-blue-800' :
                            f.status === 'concluida' ? 'bg-green-100 text-green-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {f.status}
                          </Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">Nenhuma programação de férias</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* ============= DIALOG DOCUMENTO ============= */}
      <Dialog open={showDocumentoDialog} onOpenChange={setShowDocumentoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Documento</DialogTitle>
            {selectedFuncionario && (
              <DialogDescription>
                Funcionário: <strong>{selectedFuncionario.nome}</strong>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            {/* Seletor de funcionário caso não tenha selecionado */}
            {!selectedFuncionario && (
              <div>
                <Label>Funcionário *</Label>
                <Select
                  value={selectedFuncionario?.id || ''}
                  onValueChange={(value) => {
                    const func = funcionarios.find(f => f.id === value);
                    setSelectedFuncionario(func);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o funcionário" />
                  </SelectTrigger>
                  <SelectContent>
                    {funcionarios.filter(f => f.ativo).map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Tipo de Documento *</Label>
              <Select
                value={documentoForm.tipo}
                onValueChange={(value) => setDocumentoForm(prev => ({ ...prev, tipo: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(tipoDocumentoLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome do Documento *</Label>
              <Input
                value={documentoForm.nome}
                onChange={(e) => setDocumentoForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: RG - João Silva"
              />
            </div>
            <div>
              <Label>Data de Validade</Label>
              <Input
                type="date"
                value={documentoForm.data_validade}
                onChange={(e) => setDocumentoForm(prev => ({ ...prev, data_validade: e.target.value }))}
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={documentoForm.descricao}
                onChange={(e) => setDocumentoForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Observações sobre o documento"
                rows={2}
              />
            </div>
            <div>
              <Label>Arquivo</Label>
              <Input
                type="file"
                onChange={(e) => setDocumentoForm(prev => ({ ...prev, arquivo: e.target.files?.[0] || null }))}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <p className="text-xs text-slate-500 mt-1">Formatos: PDF, JPG, PNG</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentoDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddDocumento}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= DIALOG ATESTADO ============= */}
      <Dialog open={showAtestadoDialog} onOpenChange={setShowAtestadoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Atestado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={atestadoForm.data_inicio}
                  onChange={(e) => setAtestadoForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data Fim *</Label>
                <Input
                  type="date"
                  value={atestadoForm.data_fim}
                  onChange={(e) => setAtestadoForm(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Dias de Afastamento</Label>
              <Input
                type="number" onFocus={(e) => e.target.select()}
                value={atestadoForm.dias_afastamento}
                onChange={(e) => setAtestadoForm(prev => ({ ...prev, dias_afastamento: parseInt(e.target.value) || 1 }))}
                min={1}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CID</Label>
                <Input
                  value={atestadoForm.cid}
                  onChange={(e) => setAtestadoForm(prev => ({ ...prev, cid: e.target.value }))}
                  placeholder="Ex: J11"
                />
              </div>
              <div>
                <Label>Descrição CID</Label>
                <Input
                  value={atestadoForm.descricao_cid}
                  onChange={(e) => setAtestadoForm(prev => ({ ...prev, descricao_cid: e.target.value }))}
                  placeholder="Ex: Gripe"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome do Médico</Label>
                <Input
                  value={atestadoForm.nome_medico}
                  onChange={(e) => setAtestadoForm(prev => ({ ...prev, nome_medico: e.target.value }))}
                />
              </div>
              <div>
                <Label>CRM</Label>
                <Input
                  value={atestadoForm.crm_medico}
                  onChange={(e) => setAtestadoForm(prev => ({ ...prev, crm_medico: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea
                value={atestadoForm.observacoes}
                onChange={(e) => setAtestadoForm(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAtestadoDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddAtestado}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= DIALOG FÉRIAS ============= */}
      <Dialog open={showFeriasDialog} onOpenChange={setShowFeriasDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Programar Férias</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Funcionário *</Label>
              <Select
                value={feriasForm.funcionario_id}
                onValueChange={(value) => setFeriasForm(prev => ({ ...prev, funcionario_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o funcionário" />
                </SelectTrigger>
                <SelectContent>
                  {funcionarios.filter(f => f.ativo).map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Período Aquisitivo - Início</Label>
                <Input
                  type="date"
                  value={feriasForm.periodo_aquisitivo_inicio}
                  onChange={(e) => setFeriasForm(prev => ({ ...prev, periodo_aquisitivo_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Período Aquisitivo - Fim</Label>
                <Input
                  type="date"
                  value={feriasForm.periodo_aquisitivo_fim}
                  onChange={(e) => setFeriasForm(prev => ({ ...prev, periodo_aquisitivo_fim: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={feriasForm.data_inicio}
                  onChange={(e) => setFeriasForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data Fim *</Label>
                <Input
                  type="date"
                  value={feriasForm.data_fim}
                  onChange={(e) => setFeriasForm(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dias de Gozo</Label>
                <Input
                  type="number" onFocus={(e) => e.target.select()}
                  value={feriasForm.dias_gozo}
                  onChange={(e) => setFeriasForm(prev => ({ ...prev, dias_gozo: parseInt(e.target.value) || 30 }))}
                  min={0}
                  max={30}
                />
              </div>
              <div>
                <Label>Dias de Abono</Label>
                <Input
                  type="number" onFocus={(e) => e.target.select()}
                  value={feriasForm.dias_abono}
                  onChange={(e) => setFeriasForm(prev => ({ ...prev, dias_abono: parseInt(e.target.value) || 0 }))}
                  min={0}
                  max={10}
                />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea
                value={feriasForm.observacoes}
                onChange={(e) => setFeriasForm(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeriasDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateFerias}>Programar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= DIALOG TREINAMENTO ============= */}
      <Dialog open={showTreinamentoDialog} onOpenChange={setShowTreinamentoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Treinamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={treinamentoForm.titulo}
                onChange={(e) => setTreinamentoForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Nome do treinamento"
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={treinamentoForm.descricao}
                onChange={(e) => setTreinamentoForm(prev => ({ ...prev, descricao: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Instrutor</Label>
                <Input
                  value={treinamentoForm.instrutor}
                  onChange={(e) => setTreinamentoForm(prev => ({ ...prev, instrutor: e.target.value }))}
                />
              </div>
              <div>
                <Label>Local</Label>
                <Input
                  value={treinamentoForm.local}
                  onChange={(e) => setTreinamentoForm(prev => ({ ...prev, local: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={treinamentoForm.data_inicio}
                  onChange={(e) => setTreinamentoForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={treinamentoForm.data_fim}
                  onChange={(e) => setTreinamentoForm(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Carga Horária (horas)</Label>
              <Input
                type="number" onFocus={(e) => e.target.select()}
                value={treinamentoForm.carga_horaria}
                onChange={(e) => setTreinamentoForm(prev => ({ ...prev, carga_horaria: parseInt(e.target.value) || 0 }))}
                min={0}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTreinamentoDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateTreinamento}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
